import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";

// Optimized images via vite-imagetools - different sizes for mobile/desktop
import heroMobile from "@/assets/easy-taxes-hero-devices.png?w=520&format=webp";
import heroDesktop from "@/assets/easy-taxes-hero-devices.png?w=1240&format=webp";

export const Hero = () => {
  const { trackCTA } = useGoogleAnalytics();

  return (
    <section 
      className="relative pt-16 pb-10 md:pt-24 md:pb-12 lg:pt-28 lg:pb-16 min-h-0 lg:min-h-[80vh] overflow-hidden mt-14 md:mt-16 bg-transparent"
      aria-labelledby="hero-title"
    >
      {/* Subtle halo effect behind content */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[800px] h-[500px] md:h-[800px] rounded-full pointer-events-none"
        aria-hidden="true"
        style={{
          background: 'radial-gradient(circle, rgba(220, 228, 222, 0.25) 0%, transparent 70%)'
        }}
      />
      
      <div className="container-custom relative z-10 px-4">
        <div className="grid md:grid-cols-[1fr_1fr] lg:grid-cols-[1fr_1.1fr] gap-4 md:gap-8 lg:gap-10 items-center md:justify-items-center">
          {/* Left Column - Text Content */}
          <div className="flex flex-col justify-center h-full space-y-4 md:space-y-5 animate-fade-in-up text-left">
            {/* Eyebrow */}
            <h2 className="text-[11px] md:text-xs lg:text-sm tracking-[0.08em] md:tracking-[0.12em] uppercase font-normal"
              style={{ color: 'rgba(11, 59, 50, 0.7)' }}
            >
              Easy Taxes <span className="font-normal" style={{ color: 'rgba(11, 59, 50, 0.5)' }}>par</span> ELYX Finance
            </h2>
            
            {/* H1 - LCP element - NO animation delay */}
            <h1 
              id="hero-title"
              className="text-[1.75rem] leading-[1.2] sm:text-[1.85rem] md:text-[1.9rem] lg:text-[2.2rem] xl:text-[2.5rem] font-semibold font-heading tracking-tight"
              style={{ color: '#0A3830' }}
            >
              Les impôts, sans stress. Enfin.
            </h1>
            
            {/* Subtitle - NO animation delay for LCP */}
            <div className="space-y-0">
              <p className="text-sm md:text-base lg:text-lg font-body leading-relaxed"
                style={{ color: 'rgba(11, 59, 50, 0.75)' }}
              >
                Une interface claire, pour savoir où vous en êtes.
              </p>
              <p className="text-sm md:text-base lg:text-lg font-body leading-relaxed"
                style={{ color: 'rgba(11, 59, 50, 0.75)' }}
              >
                Un interlocuteur fiscal dédié.
              </p>
              <p className="text-sm md:text-base lg:text-lg font-body leading-relaxed"
                style={{ color: 'rgba(11, 59, 50, 0.75)' }}
              >
                Une déclaration encadrée, validée avant envoi.
              </p>
            </div>

            {/* CTA Button - Mobile optimized tap target */}
            <div className="pt-2 md:pt-2 flex justify-center md:justify-start">
              <a 
                href="https://taxes.elyx-finance.ch/auth?tab=signup" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 rounded-xl"
                aria-label="Créer mon espace sécurisé Easy Taxes (ouvre dans un nouvel onglet)"
                onClick={() => trackCTA('cta_click_signup', 'hero_main', 'hero')}
              >
                <Button 
                  variant="cta" 
                  size="lg"
                  className="text-green-primary md:text-white text-sm md:text-base font-semibold tracking-wide px-6 md:px-8 py-4 md:py-6 min-h-[48px] md:min-h-[48px] cta-scroll-animate"
                  style={{ backgroundColor: '#BFA06A' }}
                >
                  <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                  Créer mon espace sécurisé
                </Button>
              </a>
            </div>

            {/* Micro-text under CTA */}
            <p className="text-[11px] md:text-[13px] leading-relaxed"
              style={{ color: 'rgba(11, 59, 50, 0.7)' }}
            >
              ✓ Accès gratuit · Tarif estimatif avant validation · Sans engagement
            </p>

            {/* Questions block - Hidden on mobile */}
            <div className="hidden lg:block pt-1">
              <a 
                href="https://calendly.com/decouverte-elyx-finance/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-block min-h-[44px] flex items-center justify-start focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 rounded-md"
                aria-label="Réserver un appel découverte gratuit avec un expert (ouvre dans un nouvel onglet)"
                onClick={() => trackCTA('cta_click_calendly', 'hero_calendly', 'hero')}
              >
                <div>
                  <p className="text-[13px] font-medium transition-colors"
                    style={{ color: 'rgba(10, 56, 48, 0.65)' }}
                  >
                    Une question avant de commencer ?
                  </p>
                  <p className="text-[13px] transition-colors group-hover:underline"
                    style={{ color: 'rgba(11, 59, 50, 0.55)' }}
                  >
                    Un conseiller fiscal vous rappelle – entretien offert.
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Mobile image */}
          <div className="flex md:hidden justify-center mt-6">
            <img 
              src={heroMobile} 
              alt="Interface Easy Taxes affichée sur ordinateur portable et smartphone montrant le tableau de bord de déclaration fiscale"
              role="img"
              className="w-full max-w-[260px] h-auto"
              width={260}
              height={203}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              style={{ 
                filter: 'drop-shadow(0 15px 30px rgba(11, 59, 50, 0.12))',
              }}
            />
          </div>

          {/* Desktop version - simple CSS hover effect instead of JS parallax */}
          <div 
            className="hidden md:flex items-center justify-center group relative transition-transform duration-300 ease-out hover:scale-[1.02]"
          >
            {/* Golden glow behind desktop */}
            <div 
              className="absolute inset-0 -z-10"
              aria-hidden="true"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(201, 164, 76, 0.18) 0%, rgba(201, 164, 76, 0.08) 40%, transparent 70%)',
                filter: 'blur(50px)',
                transform: 'scale(1.4) translateY(10px)'
              }}
            />
            <img 
              src={heroDesktop} 
              alt="Interface Easy Taxes affichée sur ordinateur portable et smartphone montrant le tableau de bord de déclaration fiscale"
              role="img"
              width={620}
              height={484}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="w-full max-w-[380px] lg:max-w-[550px] xl:max-w-[620px] h-auto transition-all duration-500 ease-out group-hover:scale-[1.02]"
              style={{ 
                filter: 'drop-shadow(0 30px 60px rgba(11, 59, 50, 0.15)) drop-shadow(0 15px 30px rgba(11, 59, 50, 0.1))',
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};